import * as React from 'react';
import { FilterAnchor, type FilterLink } from '@chess/api-insights/charts';

// Bars for the Blunders and Misses cards: one row per group of mistakes, its
// label and its bar each opening the group's games in the library
// (`mistake=<key>`), the bar's value at its right and, optionally, a second
// figure in its own column (the games beside a cost, the cost beside the
// games). A row with nothing in it is muted and opens nothing. Drawn from
// props only.

export interface BarRow {
  /** The library key the row opens, `<kind>-<dimension>-<value>`. */
  key: string;
  label: string;
  /** The bar's length, scaled to the largest row. */
  value: number;
  /** The value as written at the bar's end. */
  valueText: string;
  /** The second figure, in its own column. */
  extra?: string;
  /** Games behind the row: zero makes it muted and not a link. */
  games: number;
  /** A CSS colour for the bar. */
  colour: string;
  title?: string;
}

export const GroupBars: React.FC<{ rows: BarRow[]; link?: FilterLink; label?: string }> = ({ rows, link, label }) => {
  const max = Math.max(...rows.map((r) => r.value), 0) || 1;
  const extra = rows.some((r) => r.extra !== undefined);
  return (
    <div className={`mistake-bars${extra ? ' mistake-bars-extra' : ''}`} role="list" aria-label={label}>
      {rows.map((r) => {
        const to = r.games ? link : undefined;
        const filter = { mistake: r.key };
        return (
          <div key={r.key} role="listitem" className={`mistake-bar-row${r.games ? '' : ' mistake-bar-none'}`} title={r.title}>
            <FilterAnchor className="mistake-bar-label" link={to} filter={filter}>
              {r.label}
            </FilterAnchor>
            <span className="mistake-bar-track">
              {r.value > 0 && (
                <FilterAnchor className="mistake-bar-fill" link={to} filter={filter} style={{ width: `${(100 * r.value) / max}%`, background: r.colour }}>
                  {null}
                </FilterAnchor>
              )}
            </span>
            <span className="mistake-bar-value">{r.valueText}</span>
            {extra && <span className="mistake-bar-extra">{r.extra ?? ''}</span>}
          </div>
        );
      })}
    </div>
  );
};
